import { useEffect, useRef, useState } from 'react';
import { getFieldErrors, isApiError } from '@/services/api';
import { useSubmitInvoiceMutation } from './useSubmitInvoiceMutation';
import { isFundingApprovalRejection, readInvoiceRejection } from '../services/invoiceRejection';
import {
    emptyTargets,
    invoiceRejectionMessages,
    promoteUnrenderableLineMessages,
} from '../utils/invoiceRejectionMessages';
import type { MessagesByTarget } from '../utils/invoiceRejectionMessages';
import { toInvoiceLines } from '../utils/toInvoiceLines';
import { toSubmitPayload } from '../schemas/invoiceSubmit.schema';
import type { InvoiceSubmitValues } from '../schemas/invoiceSubmit.schema';
import type { FundingOverridePayload } from '../types/agreements.types';
import type { InvoiceSelection } from './useInvoiceSelection';

export type SubmitOutcome =
    | { ok: true }
    | { ok: false; fieldErrors: Record<string, string[]>; lineRejected: boolean };

interface SubmissionState {
    agreementId: string;
    reasonsByTarget: MessagesByTarget;
    lineReasons: Map<string, string>;
    lastSubmittedNumber: string | null;
}

export interface InvoiceSubmission {
    submit: (values: InvoiceSubmitValues) => Promise<SubmitOutcome>;
    isPending: boolean;
    /** The submitted claim has not reached the held cash position yet. */
    settling: boolean;
    reasonsByTarget: MessagesByTarget;
    lineReasons: Map<string, string>;
    lastSubmittedNumber: string | null;
    dismissNotice: () => void;
}

const UPSTREAM_UNAVAILABLE_MESSAGE = 'This service is temporarily unavailable. Try again.';
const GENERIC_SUBMIT_FAILURE_MESSAGE = 'This invoice could not be submitted. Try again.';
const FORM_FIELD_KEYS = ['invoice_number', 'invoiced_at', 'invoicing_dealer_id', 'mileage'];

function mergeFieldErrors(a: Record<string, string[]>, b: Record<string, string[]>): Record<string, string[]> {
    const merged: Record<string, string[]> = { ...a };
    for (const [key, messages] of Object.entries(b)) {
        merged[key] = [...(merged[key] ?? []), ...messages];
    }
    return merged;
}

/** Non-form keys carry their own copy whether or not a form field also failed, so a
 *  `lines` message is never swallowed by an accompanying `invoice_number` error. */
function agreementLevelMessages(error: unknown, fieldErrors: Record<string, string[]>): string[] {
    if (isApiError(error) && error.status === 502) return [UPSTREAM_UNAVAILABLE_MESSAGE];

    const nonFormMessages =
        isApiError(error) && error.status === 422
            ? Object.entries(fieldErrors)
                  .filter(([key]) => !FORM_FIELD_KEYS.includes(key))
                  .flatMap(([, messages]) => messages)
            : [];
    if (nonFormMessages.length > 0) return nonFormMessages;

    const hasDisplayableField = Object.keys(fieldErrors).some((key) => FORM_FIELD_KEYS.includes(key));
    return hasDisplayableField ? [] : [GENERIC_SUBMIT_FAILURE_MESSAGE];
}

const emptyState = (agreementId: string): SubmissionState => ({
    agreementId,
    reasonsByTarget: emptyTargets(),
    lineReasons: new Map(),
    lastSubmittedNumber: null,
});

/** Owns the mutation, maps every failure shape to copy, clears the submitted
 *  lines and resets the reasons on success. Notices never cross an agreement. */
export function useInvoiceSubmission(
    agreementId: string,
    invoiceSelection: InvoiceSelection,
    override?: FundingOverridePayload,
    onFundingRejected?: () => void,
): InvoiceSubmission {
    const mutation = useSubmitInvoiceMutation(agreementId);
    const [state, setState] = useState<SubmissionState>(() => emptyState(agreementId));

    // Tracked-previous-value pattern, as in `useInvoiceSelection`: re-key to empty
    // during render when the agreement id changes, with no effect required.
    let current = state;
    if (state.agreementId !== agreementId) {
        current = emptyState(agreementId);
        setState(current);
    }

    // A response that lands after the agreement changed must not write to the new one.
    const writeState = (patch: Partial<Omit<SubmissionState, 'agreementId'>>): void => {
        setState((previous) => (previous.agreementId === agreementId ? { ...previous, ...patch } : previous));
    };

    // The rejection arrives after the closure's selection is stale, so the rows a
    // message can still render against are read from the latest render.
    const liveRows = useRef(invoiceSelection.totals.rows);
    useEffect(() => {
        liveRows.current = invoiceSelection.totals.rows;
    }, [invoiceSelection.totals.rows]);

    const submit = async (values: InvoiceSubmitValues): Promise<SubmitOutcome> => {
        // The snapshot guards an edit or a refetch landing mid-flight.
        const submittedUnits = invoiceSelection.selection;
        const submittedRows = invoiceSelection.totals.rows;
        const lines = toInvoiceLines(submittedUnits);
        const payload = toSubmitPayload(values, lines, override);

        writeState({ lastSubmittedNumber: null });

        try {
            const invoice = await mutation.mutateAsync(payload);
            writeState({
                reasonsByTarget: emptyTargets(),
                lineReasons: new Map(),
                lastSubmittedNumber: invoice.invoiceNumber,
            });
            invoiceSelection.clearUnits(submittedUnits);
            return { ok: true };
        } catch (error) {
            const fieldErrors = getFieldErrors(error);
            const rejections = readInvoiceRejection(error);

            if (rejections.length > 0) {
                const messages = promoteUnrenderableLineMessages(
                    invoiceRejectionMessages(rejections, lines, submittedRows),
                    new Set(liveRows.current.map((row) => row.key)),
                );
                writeState({ reasonsByTarget: messages.byTarget, lineReasons: messages.byLineKey });
                if (isFundingApprovalRejection(rejections)) onFundingRejected?.();
                return {
                    ok: false,
                    fieldErrors: mergeFieldErrors(fieldErrors, messages.fieldErrors),
                    lineRejected: messages.byLineKey.size > 0,
                };
            }

            writeState({
                lineReasons: new Map(),
                reasonsByTarget: { ...emptyTargets(), submit: agreementLevelMessages(error, fieldErrors) },
            });
            return { ok: false, fieldErrors, lineRejected: false };
        }
    };

    return {
        submit,
        isPending: mutation.isPending,
        settling: mutation.settling,
        reasonsByTarget: current.reasonsByTarget,
        lineReasons: current.lineReasons,
        lastSubmittedNumber: current.lastSubmittedNumber,
        dismissNotice: () => writeState({ lastSubmittedNumber: null }),
    };
}
